// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import { Mail, Loader2, Check, AlertTriangle } from 'lucide-react'
import * as api from '../../api/client'

type Scope = { kind: 'pharmacy'; pharmacyId: string } | { kind: 'platform' }

/**
 * Reusable "sender email" editor (the outbound From identity) used both for a specific
 * pharmacy (admin-assigned) and for the admin/management portal. Config only — actual mail
 * delivery still depends on transport credentials being wired (shown via transport_status).
 */
export default function EmailSenderConfig({
  title, description, scope,
}: { title: string; description: string; scope: Scope }) {
  const [cfg, setCfg] = useState<api.EmailConfig | null>(null)
  const [form, setForm] = useState({ from_email: '', sender_name: '', reply_to: '', notifications_enabled: true })
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  const scopeKey = scope.kind === 'pharmacy' ? scope.pharmacyId : 'platform'
  useEffect(() => {
    let live = true
    const p = scope.kind === 'platform' ? api.getPlatformEmailConfig() : api.getPharmacyEmailConfig(scope.pharmacyId)
    p.then(c => {
      if (!live) return
      setCfg(c)
      setForm({
        from_email: c.from_email ?? '', sender_name: c.sender_name ?? '',
        reply_to: c.reply_to ?? '', notifications_enabled: c.notifications_enabled,
      })
    }).catch(() => { if (live) setErr('Could not load email settings.') })
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey])

  function set<K extends keyof typeof form>(k: K, v: (typeof form)[K]) { setForm(f => ({ ...f, [k]: v })) }

  async function onSave() {
    setBusy(true); setMsg(null); setErr(null)
    const input: api.EmailConfigInput = {
      from_email: form.from_email.trim() || null,
      sender_name: form.sender_name.trim() || null,
      reply_to: form.reply_to.trim() || null,
      notifications_enabled: form.notifications_enabled,
    }
    try {
      if (scope.kind === 'platform') await api.putPlatformEmailConfig(input)
      else await api.putPharmacyEmailConfig(scope.pharmacyId, input)
      setMsg('Saved.')
    } catch {
      setErr('Save failed.')
    } finally { setBusy(false) }
  }

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Mail size={16} aria-hidden="true" />
        <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>{title}</h3>
      </div>
      <p style={{ fontSize: 12, color: 'var(--color-text-mute)', margin: '0 0 12px' }}>{description}</p>

      <div style={{ display: 'grid', gap: 10, maxWidth: 460 }}>
        <L label="From address"><input style={inp} type="email" value={form.from_email}
          onChange={e => set('from_email', e.target.value)} placeholder="notifications@yourdomain.com" /></L>
        <L label="Sender display name"><input style={inp} value={form.sender_name}
          onChange={e => set('sender_name', e.target.value)} placeholder="Apple Specialty Pharmacy" /></L>
        <L label="Reply-to (optional)"><input style={inp} type="email" value={form.reply_to}
          onChange={e => set('reply_to', e.target.value)} placeholder="support@yourdomain.com" /></L>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <input type="checkbox" checked={form.notifications_enabled}
            onChange={e => set('notifications_enabled', e.target.checked)} />
          Notifications enabled
        </label>
      </div>

      {cfg && cfg.transport_status === 'delivery_pending_transport_config' && (
        <div style={warn}>
          <AlertTriangle size={14} aria-hidden="true" />
          <span>Sender identity is saved, but mail will not actually send until a transport (SMTP/SendGrid) and credentials are configured.</span>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
        <button style={primary} disabled={busy} onClick={onSave}>
          {busy ? <Loader2 className="spin" size={14} /> : <Check size={14} />} Save sender email
        </button>
        {msg && <span style={{ fontSize: 12, color: 'var(--color-accent, #0d9488)' }}>{msg}</span>}
        {err && <span style={{ fontSize: 12, color: 'var(--color-danger, #dc2626)' }}>{err}</span>}
      </div>
    </div>
  )
}

function L({ label, children }: { label: string; children: React.ReactNode }) {
  return <label style={{ display: 'block', fontSize: 12 }}>
    <span style={{ color: 'var(--color-text-mute)', display: 'block', marginBottom: 4 }}>{label}</span>{children}
  </label>
}

const card: React.CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 12, padding: 16, background: 'var(--color-surface)', marginTop: 16 }
const inp: React.CSSProperties = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)', fontSize: 13, fontFamily: 'inherit' }
const primary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: 'none', background: 'var(--color-accent, #0d9488)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }
const warn: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, padding: '8px 10px', borderRadius: 8, background: 'var(--color-warning-soft, #fef3c7)', color: 'var(--color-warning-text, #92400e)', fontSize: 12 }
